/**
 * Kiosk Player Page
 * Full-screen playback with WebSocket sync and lease management
 */

import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRoomWebSocket } from '../hooks/useRoomWebSocket';
import { useMediaLease } from '../hooks/useMediaLease';
import { usePlayerStore, useCurrentItem, useItemsToPreload } from '../stores/playerStore';
import { MediaRenderer } from '../components/MediaRenderer';
import { PRELOAD_AHEAD_COUNT } from '@gallery/shared';

export default function Player() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const [screenId] = useState(() => crypto.randomUUID());

  // Player store
  const {
    isConnected,
    isPaired,
    playlist,
    isPlaying,
    showQRCode,
    error,
    setRoomState,
    setConnected,
    setPaired,
    setPlaylist,
    nextItem,
    setPlaying,
    setCurrentTimeMs,
    setPreloadedUrl,
    setShowQRCode,
    setError,
  } = usePlayerStore();

  const currentItem = useCurrentItem();
  const itemsToPreload = useItemsToPreload(PRELOAD_AHEAD_COUNT);

  // Media lease management
  const { requestLease, requestBatchLeases, getSignedUrl } = useMediaLease({
    roomCode,
    onLeaseError: (artworkId, err) => {
      console.error(`Lease error for ${artworkId}:`, err);
    },
  });

  // WebSocket connection
  const { roomState } = useRoomWebSocket({
    roomCode: roomCode || '',
    screenId,
    onStateChange: (state) => {
      setRoomState(state);
      setPlaying(!state.isPaused);
    },
    onPaired: (claimed) => {
      setPaired(claimed);
      if (claimed) {
        setShowQRCode(false);
      }
    },
    onError: setError,
  });

  // Track connection state
  useEffect(() => {
    setConnected(!!roomState);
  }, [roomState, setConnected]);

  // Fetch playlist when playlistId changes
  useEffect(() => {
    if (!roomState?.playlistId) return;

    const fetchPlaylist = async () => {
      try {
        const response = await fetch(`/api/v1/playlists/${roomState.playlistId}`, {
          credentials: 'include',
        });
        const data = await response.json();

        if (data.success && data.data?.items) {
          setPlaylist(data.data.items);

          // Request leases for all items
          const artworkIds = data.data.items.map((item: { artworkId: string }) => item.artworkId);
          await requestBatchLeases(artworkIds);
        }
      } catch (err) {
        console.error('Failed to fetch playlist:', err);
        setError('Failed to load playlist');
      }
    };

    fetchPlaylist();
  }, [roomState?.playlistId, setPlaylist, requestBatchLeases, setError]);

  // Preload upcoming items
  useEffect(() => {
    for (const item of itemsToPreload) {
      if (!getSignedUrl(item.artworkId)) {
        requestLease(item.artworkId).then((lease) => {
          if (lease) {
            setPreloadedUrl(item.artworkId, lease.signedUrl);

            // Preload image
            if (item.artworkType === 'image') {
              const img = new Image();
              img.src = lease.signedUrl;
            }
          }
        });
      }
    }
  }, [itemsToPreload, getSignedUrl, requestLease, setPreloadedUrl]);

  // Handle item ended - advance to next
  const handleItemEnded = useCallback(() => {
    nextItem();
  }, [nextItem]);

  // Handle time updates
  const handleTimeUpdate = useCallback(
    (timeMs: number) => {
      setCurrentTimeMs(timeMs);
    },
    [setCurrentTimeMs]
  );

  // Get signed URL for current item
  const currentUrl = useMemo(() => {
    if (!currentItem) return null;
    return getSignedUrl(currentItem.artworkId);
  }, [currentItem, getSignedUrl]);

  // Request fullscreen on mount
  useEffect(() => {
    const requestFullscreen = async () => {
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        console.log('Fullscreen not available');
      }
    };

    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'f':
        case 'F':
          requestFullscreen();
          break;
        case 'Escape':
          // Exit fullscreen handled by browser
          break;
        case ' ':
          e.preventDefault();
          setPlaying(!isPlaying);
          break;
        case 'ArrowRight':
          nextItem();
          break;
        case 'ArrowLeft':
          // Previous item
          usePlayerStore.getState().prevItem();
          break;
        case 'q':
        case 'Q':
          setShowQRCode(!showQRCode);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, showQRCode, nextItem, setPlaying, setShowQRCode]);

  // Prevent screen sleep
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch {
        console.log('Wake lock not available');
      }
    };

    requestWakeLock();

    // Re-acquire wake lock on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release();
    };
  }, []);

  // Controller pairing URL
  const controllerUrl = `${window.location.origin}/control/${roomCode}`;

  // Show QR code when not paired or no playlist
  if (showQRCode || !isPaired || playlist.length === 0) {
    return (
      <div className="player-fullscreen flex items-center justify-center bg-black">
        <div className="player-overlay bg-white rounded-2xl p-8 shadow-2xl max-w-sm mx-4">
          <div className="player-qr text-center">
            <h2 className="text-gray-900 text-2xl font-bold mb-4">Scan to Control</h2>
            <QRCodeSVG
              value={controllerUrl}
              size={256}
              level="H"
              includeMargin
              className="mx-auto mb-4"
            />
            <p className="text-gray-600 text-lg font-mono mb-2">{roomCode}</p>
            <p className="text-gray-400 text-sm">Or visit: {controllerUrl}</p>

            {/* Connection status */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
              />
              <span className="text-gray-500 text-sm">
                {isConnected ? 'Connected' : 'Connecting...'}
              </span>
            </div>

            {/* Error display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Demo button */}
            {playlist.length > 0 && (
              <button
                onClick={() => {
                  setPaired(true);
                  setShowQRCode(false);
                }}
                className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Start Playback
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main playback view
  return (
    <div className="player-fullscreen bg-black">
      {/* Media display */}
      {currentItem && currentUrl && (
        <MediaRenderer
          key={currentItem.id}
          url={currentUrl}
          type={currentItem.artworkType}
          durationMs={currentItem.durationMs}
          transition={currentItem.transition as 'fade' | 'cut' | 'slide'}
          isPlaying={isPlaying}
          onEnded={handleItemEnded}
          onTimeUpdate={handleTimeUpdate}
          onError={(err) => setError(err)}
          className="absolute inset-0"
        />
      )}

      {/* Loading state when no URL */}
      {currentItem && !currentUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50">Loading...</p>
          </div>
        </div>
      )}

      {/* No content state */}
      {!currentItem && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-white text-4xl font-display mb-4">Room: {roomCode}</h1>
            <p className="text-white/50">Waiting for playlist...</p>
          </div>
        </div>
      )}

      {/* Overlay controls (shown on hover/tap) */}
      <div className="player-controls absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent opacity-0 hover:opacity-100 transition-opacity">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setPlaying(!isPlaying)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30"
            >
              {isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => usePlayerStore.getState().prevItem()}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z" />
              </svg>
            </button>
            <button
              onClick={nextItem}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zm8.5-6l8.5 6V6l-8.5 6z" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-4">
            {currentItem && (
              <span className="text-white/70 text-sm">
                {usePlayerStore.getState().currentIndex + 1} / {playlist.length}
              </span>
            )}
            <button
              onClick={() => setShowQRCode(true)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30"
              title="Show QR Code"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 11V3h8v8H3zm2-6v4h4V5H5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21v-8h8v8H3zm2-6v4h4v-4H5zm8 6v-2h2v2h-2zm2-4v-2h2v2h-2zm2 4v-2h2v2h-2zm2-4v-2h2v2h-2zm-2-2v-2h2v2h-2zm2 0h2v2h-2v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="absolute top-4 right-4 text-white/30 text-xs">
        F: Fullscreen | Space: Play/Pause | ←→: Navigate | Q: QR Code
      </div>
    </div>
  );
}
